import { Box3, Vector2Tuple, Vector3 } from 'three';

import {
  pickSchemaFields,
  Schema,
  SchemaFieldEnum,
  SchemaFieldFurniture,
  SchemaFieldNumber,
  SchemaFields,
  SchemaValues,
} from '../schema/index.js';
import { ChairsOrientation, FInstanceNode } from '../types/index.js';
import { angleForChairsOrientation } from '../utils/angleForChairsOrientation.js';

import { FEnsembleBase } from './FEnsembleBase.js';

const TAU = Math.PI * 2;

export interface FEnsembleCabaretSchemaFields extends SchemaFields {
  chairType: SchemaFieldFurniture;
  tableType: SchemaFieldFurniture;
  chairsPerTable: SchemaFieldNumber;
  chairFanOffset: SchemaFieldNumber;
  chairFanRadius: SchemaFieldNumber;
  chairDistanceFromTable: SchemaFieldNumber;
  chairsGap: SchemaFieldNumber;
  chairsOrientation: SchemaFieldEnum<ChairsOrientation>;
}

export interface FEnsembleCabaretSchema extends Schema {
  fields: FEnsembleCabaretSchemaFields;
}

export interface FEnsembleCabaretValues {
  tableRadius: number;
  chairSize: Vector2Tuple;
  maxChairsPerTable: number;
}

export type FEnsembleCabaretAttributes = Partial<SchemaValues<FEnsembleCabaretSchemaFields>> &
  FEnsembleCabaretValues;

export class FEnsembleCabaret extends FEnsembleBase<
  FEnsembleCabaretSchema,
  FEnsembleCabaretValues
> {
  constructor(schema: FEnsembleCabaretSchema, initialValues?: Partial<FEnsembleCabaretValues>) {
    super(
      {
        ...schema,
        fields: pickSchemaFields(schema, [
          'chairType',
          'tableType',
          'chairsPerTable',
          'chairFanOffset',
          'chairFanRadius',
          'chairDistanceFromTable',
          'chairsGap',
        ]) as any,
      },
      {
        chairSize: [0, 0],
        tableRadius: 0,
        maxChairsPerTable: 0,
        ...initialValues,
      }
    );
  }

  getNodeTypes() {
    const { chairType, tableType } = this.getValues();

    if (!chairType) {
      throw new Error('chairType is required');
    }

    if (!tableType) {
      throw new Error('tableType is required');
    }

    return [chairType, tableType];
  }

  updateObjectMeasurements() {
    const [chair, table] = this.getObjects();

    if (!chair) {
      throw new Error('chair table object');
    }

    if (!table) {
      throw new Error('missing table object');
    }

    const { chairDistanceFromTable, chairFanRadius, chairsGap } = this.getValues();

    const tableBoundingBox = new Box3().setFromObject(table);
    const tableSize = tableBoundingBox.getSize(new Vector3());
    const tableRadius = Math.max(tableSize.x, tableSize.z) / 2;

    const chairBoundingBox = new Box3().setFromObject(chair);
    const chairSize = chairBoundingBox.getSize(new Vector3());
    const chairRadius = chairSize.z / 2;

    const distanceFromCenter = tableRadius + chairRadius * chairDistanceFromTable;

    const chairWidth = chairSize.x;
    const chairEdgeFromCenter = distanceFromCenter - chairRadius;

    const chairAngle = 2 * Math.atan(chairWidth / (2 * chairEdgeFromCenter));
    const chairCornerFromCenter = Math.sqrt(
      (chairWidth / 2) * (chairWidth / 2) + chairEdgeFromCenter * chairEdgeFromCenter
    );

    const chairGapAngle = 2 * Math.asin(chairsGap / (2 * chairCornerFromCenter));
    const fanAngle = TAU * chairFanRadius;
    const maxChairsPerTable = Math.floor((fanAngle + chairGapAngle) / (chairAngle + chairGapAngle));

    this.updateValues({
      tableRadius,
      chairSize: [chairSize.x, chairSize.z],
      maxChairsPerTable,
    });
  }

  computeNodes(): FInstanceNode[] {
    if (this.getObjects().length === 0) {
      return [];
    }

    let {
      tableType,
      chairType,
      chairsPerTable,
      chairFanOffset,
      chairFanRadius,
      tableRadius,
      chairSize,
      chairDistanceFromTable,
      chairsOrientation,
    } = this.getValues();

    const nodes: FInstanceNode[] = [];
    const fanAngle = TAU * chairFanRadius;
    const angleOffset = TAU * chairFanOffset;
    const chairRadius = chairSize[1] / 2;

    const distanceFromCenter = tableRadius + chairRadius * chairDistanceFromTable;

    const maxIterations = this.getSafeIterations(chairsPerTable);
    const stepAngle = fanAngle / chairsPerTable;

    const orientationOffset = angleForChairsOrientation(chairsOrientation);

    nodes.push({
      type: 'table',
      id: tableType,
      position: [0, 0],
      rotation: 0,
    });
    for (let i = 0; i < maxIterations; i++) {
      const theta = i * stepAngle + stepAngle / 2 - fanAngle / 2;

      const chairPosition = [
        distanceFromCenter * Math.cos(theta + angleOffset),
        distanceFromCenter * Math.sin(theta + angleOffset),
      ] as Vector2Tuple;

      const chairRotation =
        Math.PI / 2 -
        angleOffset -
        (chairsOrientation === ChairsOrientation.Center ? theta : orientationOffset);

      nodes.push({
        type: 'chair',
        id: chairType,
        position: chairPosition,
        rotation: chairRotation,
      });
    }

    return nodes;
  }
}
